/**
 * Entrada no portal.
 *
 * A senha nunca e guardada: vai no corpo da chamada e o que fica na sessao do navegador e o token
 * devolvido pelo backend, que por sua vez veio do Auth Reader (spec 0004, app api key `ak-`).
 *
 * O desenho e o da entrada do Auth Manager: fundo em gradiente radial, cartao em duas colunas - o
 * produto a esquerda, o formulario a direita - e o tema alternavel ja na entrada.
 */

import { Button, Card, Field, Icon, IconButton, StatusBadge } from '@ledger-glass/react'
import { useEffect, useState, type FormEvent } from 'react'

import { ApiError, setToken } from '../api/client'
import { Alert } from '../components/Alert'
import { aplicarTema, gravarTema, lerTema, type Tema } from '../shell/theme'
import { login } from './api'

export function LoginScreen({ onEntered, notice }: { onEntered: () => void; notice?: string }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tema, setTema] = useState<Tema>(lerTema)

  useEffect(() => {
    aplicarTema(tema)
    gravarTema(tema)
  }, [tema])

  async function submit(event: FormEvent) {
    event.preventDefault()
    setPending(true)
    setError(null)
    try {
      const resultado = await login(email, password)
      setToken(resultado.access_token)
      onEntered()
    } catch (exc) {
      // 401 do Auth e credencial errada; o resto e servico indisponivel - dizer qual dos dois.
      setError(
        exc instanceof ApiError && exc.status === 401
          ? 'E-mail ou senha incorretos.'
          : exc instanceof Error
            ? exc.message
            : 'Falha ao entrar.',
      )
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="auth-page">
      <header className="auth-utility-bar">
        <IconButton
          aria-pressed={tema === 'claro'}
          icon={tema === 'escuro' ? 'sun' : 'moon'}
          label={tema === 'escuro' ? 'Usar tema claro' : 'Usar tema escuro'}
          onClick={() => setTema((atual) => (atual === 'escuro' ? 'claro' : 'escuro'))}
        />
      </header>

      <main className="auth-main" id="entrar" tabIndex={-1}>
        <Card className="auth-card" tone="blue">
          <div className="auth-heading">
            <p className="eyebrow">Ecossistema Vetor 4</p>
            <div className="auth-title-lockup">
              <span className="auth-title-icon">
                <Icon name="report" size={96} />
              </span>
              <h1>Report Viewer</h1>
            </div>
            <p>Seus relatórios, no mesmo lugar: Power BI e Inteli Graph, com o recorte do seu perfil.</p>
            <StatusBadge tone="tech">Portal de relatórios</StatusBadge>
          </div>

          <div className="auth-workflow">
            {notice === undefined ? null : <Alert tone="warning">{notice}</Alert>}
            <form className="auth-form" noValidate onSubmit={submit}>
              <Field id="login-email" label="E-mail" required>
                {(props) => (
                  <input
                    {...props}
                    type="email"
                    autoComplete="username"
                    inputMode="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                )}
              </Field>

              <Field id="login-password" label="Senha" required error={error ?? undefined}>
                {(props) => (
                  <input
                    {...props}
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                )}
              </Field>

              <Button
                type="submit"
                variant="primary"
                loading={pending}
                loadingLabel="Entrando…"
                disabled={email.trim() === '' || password === ''}
              >
                Entrar
              </Button>
            </form>
            <p className="auth-support">
              Acesso pela autenticação do ecossistema: o mesmo usuário do Report Manager e do
              Inteli Graph.
            </p>
          </div>
        </Card>
      </main>
    </div>
  )
}
